import { defineStore } from 'pinia';
import type { Organisation } from '@/api/model/Organisation';
import {
    fetchOrganisationNotifications,
    resolveOrganisationNotification,
    type OrganisationNotificationBroadcast,
    type OrganisationNotificationData,
} from '@/queries/notifications';

export type { OrganisationNotificationData } from '@/queries/notifications';

const NOTIFICATION_PAGE_SIZE = 25;

interface NotificationState {
    organisationNotifications: OrganisationNotificationData[];
    totalCount: number;
    loading: boolean;
}

export const useNotificationStore = defineStore('notification', {
    state: (): NotificationState => ({
        organisationNotifications: [],
        totalCount: 0,
        loading: false,
    }),
    actions: {
        async fetchOrganisationNotifications(organisation: Organisation) {
            if (!organisation?.public_id) {
                this.organisationNotifications = [];
                this.totalCount = 0;
                return;
            }

            this.loading = true;
            try {
                const response = await fetchOrganisationNotifications(organisation);
                this.organisationNotifications = response.data;
                this.totalCount = response.meta.total;
            } catch {
                this.organisationNotifications = [];
                this.totalCount = 0;
            } finally {
                this.loading = false;
            }
        },
        applyBroadcastNotification(payload: OrganisationNotificationBroadcast) {
            this.totalCount = payload.unresolved_count;

            if (payload.notification === null) {
                return;
            }

            const existingIndex = this.organisationNotifications.findIndex(
                (notification) => notification.ulid === payload.notification?.ulid,
            );

            if (existingIndex === -1) {
                this.organisationNotifications.unshift(payload.notification);
            } else {
                this.organisationNotifications.splice(existingIndex, 1, payload.notification);
            }

            this.organisationNotifications.sort((left, right) => right.updated_at.localeCompare(left.updated_at));
            this.organisationNotifications = this.organisationNotifications.slice(0, NOTIFICATION_PAGE_SIZE);
        },
        async resolveNotification(organisation: Organisation, ulid: string) {
            await resolveOrganisationNotification(organisation, ulid);

            const index = this.organisationNotifications.findIndex((notification) => notification.ulid === ulid);
            if (index !== -1) {
                this.organisationNotifications.splice(index, 1);
            }
            this.totalCount = Math.max(0, this.totalCount - 1);
        },
        clearAll() {
            this.organisationNotifications = [];
            this.totalCount = 0;
        },
    },
});
